import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageProps } from '../types';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { droppedCitations, endsWithCitationMarker, parseCitationHref, remarkCitations, shownCitations, ShownCitation } from '../utils/citations';
import aimdocLogo from '../../logo_symbol.png';
import { AimdocMark } from '../agents/AimdocMark';
import Calendly from '../agents/actions/Calendly';
import YouTube from '../agents/actions/YouTube';
import TidyCal from '../agents/actions/TidyCal';
import HubSpotMeetings from '../agents/actions/HubSpotMeetings';
import ChiliPiper from '../agents/actions/ChiliPiper';
import * as HeroIconSolid from '@heroicons/react/24/solid'
import { formatDate } from '../utils/dateFormatter'
import Form from '../agents/actions/Form';
import { memo } from 'react';
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AppWindow, Bot, FileText, File as FileIcon, Download, Image as ImageIcon, Phone, ChevronDown, Cog, User, Sparkles, MessageCircleQuestion, AlertTriangle, CheckCircle2, Flag, Headset, CircleStop } from 'lucide-react';
import { categoryLabel as moderationCategoryLabel } from '../sessions/moderation';
import Actions from '../agents/actions/Actions';
import { markdownRenderers as baseMarkdownRenderers, CodeBlock } from '../utils/markdownRenderers';
import { CALL_CLIENT_TOOL, getToolLifecycleFromMetadata, ToolLifecycleState } from '../agents/toolLifecycle';
import ContextDrawer from '../agents/ContextDrawer';
import { assignedViaLabel, escalationRepLabel, findEscalationForToolCall, SessionEscalation } from '../agents/escalations';

interface Component {
    type: string;
    [key: string]: any;
}

type ComponentMapping = {
    calendly: typeof Calendly;
    tidycal: typeof TidyCal;
    hubspot_meetings: typeof HubSpotMeetings;
    chili_piper: typeof ChiliPiper;
    youtube: typeof YouTube;
    form: typeof Form;
    form_field: typeof Form;
    actions: typeof Actions;
};

interface MemoizedComponentProps {
    component: Component;
    config: any;
    conversationId: string;
    gettingFullResponse: boolean;
}

const parseToolPayload = (payload: any): Record<string, any> => {
    if (!payload) {
        return {};
    }
    if (typeof payload === 'string') {
        try {
            const parsed = JSON.parse(payload);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof payload === 'object' ? payload : {};
};

const getToolDisplayName = (name: string): string => {
    const names: Record<string, string> = {
        search_knowledge: 'Search Knowledge',
        search_task_definitions: 'Search Knowledge',
        web_search: 'Web Search',
        web_fetch: 'Web Fetch',
        ask_question: 'Ask Question',
        update_form: 'Update Form',
        get_company_info: 'Company Enrichment',
        set_track: 'Set Track',
        set_top_level_track: 'Set Track',
        load_task: 'Start Task',
        trigger_buyer_intent_notification: 'Notify Team',
        pause_assessment: 'Pause Assessment',
        record_assessment_response: 'Record Response',
        start_assessment: 'Start Assessment',
        escalate_to_human: 'Escalate to Human',
        show_booking_calendar: 'Show Calendar',
        navigate: 'Navigate Page',
        start_onboarding: 'Start Onboarding',
        click: 'Click Element',
        hover: 'Hover Element',
        type: 'Type into Field',
        fill: 'Fill Field',
        press_enter: 'Press Enter',
        scroll: 'Scroll Page',
        wait: 'Wait',
    };
    return names[name] || name;
};

const formatPayloadValue = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean' || typeof value === 'number') return String(value);
    return JSON.stringify(value, null, 2);
};

// A failed MCP call is the customer's own server rejecting us, so the transcript
// has to make it obvious and hand their team the reason. `error` is only ever
// populated on authenticated dashboard transcripts, never in the widget — and
// the live tool_output stream is shared with visitors, so it never carries the
// body either. `error_details_included` marks the events that could have, which
// is what separates "the server said nothing" from "not loaded yet".
const TOOL_ERROR_NO_MESSAGE = 'The tool server returned an error without a message.';
const TOOL_ERROR_NOT_LOADED = 'Error details load when this conversation is refreshed.';

const getToolErrorState = (
    output?: Record<string, any>
): { failed: boolean; detail: string; connection: string } => {
    if (!output || output.type !== 'mcp_tool_result' || output.is_error !== true) {
        return { failed: false, detail: '', connection: '' };
    }
    const connection = typeof output.connection === 'string' ? output.connection.trim() : '';
    const detail = typeof output.error === 'string' ? output.error.trim() : '';
    return {
        failed: true,
        connection,
        detail: detail
            || (output.error_details_included === true ? TOOL_ERROR_NO_MESSAGE : TOOL_ERROR_NOT_LOADED),
    };
};

// A browser tool is one the customer's own app registered through the SDK; it
// ran in the visitor's browser, not on Aimdoc or an MCP server. Every call
// shares the dispatcher name `call_client_tool`, so the card is built from the
// metadata snapshotted when the call was made and from the stored result
// envelope. The result body and error text follow the MCP rule above: present
// on dashboard transcripts, absent from the live stream.
interface ClientToolView {
    title: string;
    name: string;
    description: string;
    pageUrl: string;
    flags: string[];
    input: Record<string, any>;
    result: any;
    failed: boolean;
    failedLabel: string;
    errorDetail: string;
}

const CLIENT_TOOL_ERROR_NO_MESSAGE = 'The tool failed without a message.';

const getClientToolView = (
    tool: ToolLifecycleState,
    args: Record<string, any>,
    output: Record<string, any>
): ClientToolView | null => {
    if (tool.tool_name !== CALL_CLIENT_TOOL) {
        return null;
    }
    const snapshot = tool.client_tool || {};
    const name = [snapshot.name, args?.name, output?.tool].find(
        (value) => typeof value === 'string' && value.trim().length > 0
    ) as string | undefined || '';
    const title = snapshot.title || (typeof output?.title === 'string' ? output.title : '') || name || 'Browser tool';
    const annotations = snapshot.annotations || {};
    const flags = [
        ...(annotations.readOnlyHint ? ['Read-only'] : []),
        ...(annotations.consequentialHint ? ['Consequential'] : []),
        ...(annotations.untrustedContentHint ? ['Untrusted content'] : []),
        ...(snapshot.unregistered ? ['Not registered on the page'] : []),
    ];
    const isResult = output?.type === 'client_tool_result';
    const failed = isResult && output.is_error === true;
    const detail = typeof output?.error === 'string' ? output.error.trim() : '';
    return {
        title,
        name,
        description: snapshot.description || '',
        pageUrl: snapshot.page_url || '',
        flags,
        input: args?.arguments && typeof args.arguments === 'object' && !Array.isArray(args.arguments)
            ? args.arguments
            : {},
        result: isResult && !failed ? output.result : undefined,
        failed,
        failedLabel: output?.timed_out ? 'Timed out' : output?.interrupted ? 'Interrupted' : 'Failed',
        errorDetail: failed
            ? detail || (output.details_included === true ? CLIENT_TOOL_ERROR_NO_MESSAGE : TOOL_ERROR_NOT_LOADED)
            : '',
    };
};

// What the escalate card says once the routing result is known: who got the
// chat and why. Until the event lands (or for a call that errored) the card
// falls back to the plain tool name.
const getEscalationSummary = (escalation: SessionEscalation | null) => {
    if (!escalation) return null;
    if (escalation.status === 'missed') {
        return {
            title: 'Escalation missed',
            caption: 'No reps were available',
            missed: true,
            rep: null,
            rows: [
                ...(escalation.skill_name ? [{ label: 'Skill', value: escalation.skill_name }] : []),
            ],
        };
    }
    const repLabel = escalationRepLabel(escalation.assigned_to);
    const via = assignedViaLabel(escalation.assigned_via);
    return {
        title: repLabel ? `Escalated to ${repLabel}` : 'Escalated to the team',
        caption: [escalation.skill_name, via || (repLabel ? null : 'No rep assigned')].filter(Boolean).join(' · '),
        missed: false,
        rep: repLabel ? escalation.assigned_to || null : null,
        rows: [
            ...(repLabel ? [{ label: 'Assigned to', value: repLabel }] : []),
            ...(escalation.assigned_to?.email && escalation.assigned_to.email !== repLabel
                ? [{ label: 'Email', value: escalation.assigned_to.email }]
                : []),
            ...(via ? [{ label: 'Assigned by', value: via }] : []),
            ...(escalation.skill_name ? [{ label: 'Skill', value: escalation.skill_name }] : []),
        ],
    };
};

const ToolDetailCard = ({ toolName, args, output, rawOutput, urls, onAnalyze, failed = false, failedLabel = 'Failed', errorDetail = '', errorConnection = '', escalation = null, clientTool = null }: {
    toolName: string;
    args: Record<string, any>;
    output?: Record<string, any>;
    rawOutput?: any;
    urls?: string[];
    onAnalyze?: () => void;
    failed?: boolean;
    failedLabel?: string;
    errorDetail?: string;
    errorConnection?: string;
    escalation?: SessionEscalation | null;
    clientTool?: ClientToolView | null;
}) => {
    const [expanded, setExpanded] = useState(false);
    const escalationSummary = failed ? null : getEscalationSummary(escalation);
    const displayName = clientTool?.title || escalationSummary?.title || getToolDisplayName(toolName);
    const caption = clientTool ? "Ran in the user's browser" : escalationSummary?.caption;
    const clientToolRows = clientTool
        ? [
            ...(clientTool.name ? [{ label: 'Name', value: clientTool.name, mono: true }] : []),
            ...(clientTool.description ? [{ label: 'Description', value: clientTool.description, mono: false }] : []),
            ...(clientTool.pageUrl ? [{ label: 'Page', value: clientTool.pageUrl, mono: false }] : []),
            ...(clientTool.flags.length ? [{ label: 'Behaviour', value: clientTool.flags.join(', '), mono: false }] : []),
        ]
        : [];
    const hasArgs = Object.keys(args).length > 0;
    const hasUrls = urls && urls.length > 0;

    const outputText = (() => {
        if (!rawOutput && (!output || Object.keys(output).length === 0)) return null;
        if (typeof rawOutput === 'string' && rawOutput.length > 0) {
            try {
                return JSON.stringify(JSON.parse(rawOutput), null, 2);
            } catch {
                return rawOutput;
            }
        }
        if (output && Object.keys(output).length > 0) {
            return JSON.stringify(output, null, 2);
        }
        return null;
    })();

    const hasRouting = !!escalationSummary && escalationSummary.rows.length > 0;
    const hasDetail = hasArgs || outputText || hasUrls || failed || hasRouting || clientToolRows.length > 0;

    return (
        <div
            className={cn(
                "min-w-0 overflow-hidden rounded-md border",
                failed
                    ? "border-red-500/40 bg-red-500/[0.06] ring-1 ring-inset ring-red-500/10"
                    : "border-primary/10 bg-muted/50"
            )}
        >
            <div className="flex items-center gap-2 w-full px-3 py-1.5">
                <AimdocMark className={cn("flex-shrink-0", failed ? "text-red-600/60 dark:text-red-400/60" : "text-primary/50")} />
                <button
                    type="button"
                    className={cn(
                        "flex items-center gap-2 flex-1 min-w-0 text-left rounded",
                        hasDetail && "cursor-pointer hover:opacity-80 transition-opacity"
                    )}
                    onClick={() => hasDetail && setExpanded(!expanded)}
                    title={failed ? errorDetail : undefined}
                >
                    {failed ? (
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-600 dark:text-red-400" />
                    ) : escalationSummary?.rep ? (
                        <Avatar className="h-5 w-5 flex-shrink-0 border">
                            <AvatarImage src={escalationSummary.rep.profile_photo_url || undefined} alt={escalationSummary.title} />
                            <AvatarFallback className="text-[10px] font-medium">
                                {(escalationRepLabel(escalationSummary.rep) || '?')[0].toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                    ) : escalationSummary?.missed ? (
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                    ) : escalationSummary ? (
                        <Headset className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    ) : clientTool ? (
                        <AppWindow className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    ) : (
                        <Cog className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    )}
                    <span className={cn("text-xs font-medium truncate min-w-0", !caption && "flex-1", failed ? "text-red-700 dark:text-red-300" : "text-foreground")}>{displayName}</span>
                    {caption && (
                        <span className="text-[11px] text-muted-foreground truncate flex-1 min-w-0">{caption}</span>
                    )}
                    {failed && (
                        <span className="flex-shrink-0 rounded-full border border-red-500/20 bg-red-500/10 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-300">
                            {failedLabel}
                        </span>
                    )}
                    {hasDetail && (
                        <ChevronDown className={cn("h-3.5 w-3.5 flex-shrink-0 transition-transform", failed ? "text-red-600/70 dark:text-red-400/70" : "text-muted-foreground", expanded && "rotate-180")} />
                    )}
                </button>
                {onAnalyze && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onAnalyze}
                        className="h-6 gap-1 rounded-full bg-primary/10 px-2 text-[11px] font-medium text-primary hover:bg-primary/20 hover:text-primary flex-shrink-0 [&_svg]:size-3"
                        title="Analyze why the assistant made this tool call"
                    >
                        <Sparkles />
                        Analyze
                    </Button>
                )}
            </div>
            {hasDetail && (
                <div
                    className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
                >
                    <div className="overflow-hidden">
                        <div className={cn("border-t px-3 py-2 space-y-2 min-w-0", failed ? "border-red-500/20" : "border-border/50")}>
                            {failed && (
                                <div className="min-w-0 overflow-hidden">
                                    <div className="flex items-baseline gap-1.5 mb-1 min-w-0">
                                        <span className="text-[10px] uppercase tracking-wider font-medium text-red-700/80 dark:text-red-300/80 flex-shrink-0">Error</span>
                                        {errorConnection && (
                                            <span className="text-[10px] text-muted-foreground/70 truncate min-w-0">{errorConnection}</span>
                                        )}
                                    </div>
                                    <div className="rounded border border-red-500/20 bg-red-500/[0.07] px-2.5 py-1.5 min-w-0 overflow-hidden">
                                        <pre className="text-[11px] font-mono text-red-700 dark:text-red-300 whitespace-pre-wrap break-all leading-5 max-h-60 overflow-y-auto">{errorDetail}</pre>
                                    </div>
                                </div>
                            )}
                            {clientToolRows.length > 0 && (
                                <div className="min-w-0 overflow-hidden">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Browser tool</div>
                                    <div className="rounded bg-muted/50 px-2.5 py-1.5 min-w-0 overflow-hidden">
                                        {clientToolRows.map((row) => (
                                            <div key={row.label} className="flex gap-1.5 text-[11px] min-w-0 leading-5">
                                                <span className="text-muted-foreground/70 flex-shrink-0">{row.label}:</span>
                                                <span className={cn("text-foreground/80 min-w-0 break-all", row.mono && "font-mono")}>{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {hasRouting && (
                                <div className="min-w-0 overflow-hidden">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Routing</div>
                                    <div className="rounded bg-muted/50 px-2.5 py-1.5 min-w-0 overflow-hidden">
                                        {escalationSummary!.rows.map((row) => (
                                            <div key={row.label} className="flex gap-1.5 text-[11px] min-w-0 leading-5">
                                                <span className="text-muted-foreground/70 flex-shrink-0">{row.label}:</span>
                                                <span className="text-foreground/80 min-w-0 break-all">{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {hasArgs && (
                                <div className="min-w-0 overflow-hidden">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Input</div>
                                    <div className="rounded bg-muted/50 px-2.5 py-1.5 min-w-0 overflow-hidden">
                                        {Object.entries(args).map(([k, v]) => (
                                            <div key={k} className="flex gap-1.5 text-[11px] font-mono min-w-0 leading-5">
                                                <span className="text-muted-foreground/70 flex-shrink-0">{k}:</span>
                                                <span className="text-foreground/80 min-w-0 break-all">{formatPayloadValue(v)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(outputText || hasUrls) && (
                                <div className="min-w-0 overflow-hidden">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Output</div>
                                    <div className="rounded bg-muted/50 px-2.5 py-1.5 min-w-0 overflow-hidden space-y-1">
                                        {outputText && (
                                            <pre className="text-[11px] font-mono text-foreground/80 whitespace-pre-wrap break-all leading-5 max-h-80 overflow-y-auto">{outputText}</pre>
                                        )}
                                        {hasUrls && urls!.map((url, i) => (
                                            <a
                                                key={`${url}-${i}`}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block text-[11px] text-blue-500 hover:text-blue-700 hover:underline truncate"
                                            >
                                                {url}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Readable card for ask_question tool calls. The transcript should show what the
// visitor was actually asked — question text plus the clickable options — not the
// raw tool payload (field names, value/display pairs, persistence guidance).
const AskQuestionToolCard = ({ questions, onAnalyze }: {
    questions: Array<Record<string, any>>;
    onAnalyze?: () => void;
}) => (
    <div className="min-w-0 overflow-hidden rounded-md border border-primary/10 bg-muted/50">
        <div className="flex items-center gap-2 w-full px-3 py-1.5">
            <AimdocMark className="flex-shrink-0 text-primary/50" />
            <MessageCircleQuestion className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground truncate flex-1 min-w-0">
                {questions.length > 1 ? 'Asked questions' : 'Asked a question'}
            </span>
            {onAnalyze && (
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onAnalyze}
                    className="h-6 gap-1 rounded-full bg-primary/10 px-2 text-[11px] font-medium text-primary hover:bg-primary/20 hover:text-primary flex-shrink-0 [&_svg]:size-3"
                    title="Analyze why the assistant made this tool call"
                >
                    <Sparkles />
                    Analyze
                </Button>
            )}
        </div>
        <div className="border-t border-border/50 px-3 py-2 space-y-2 min-w-0">
            {questions.map((q, i) => (
                <div key={i} className="min-w-0">
                    <div className="text-xs text-foreground/90 break-words">{typeof q.text === 'string' ? q.text : ''}</div>
                    {Array.isArray(q.options) && q.options.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {q.options.map((opt: any, j: number) => (
                                <span
                                    key={j}
                                    className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
                                >
                                    {typeof opt === 'object' && opt !== null ? (opt.display || opt.value) : String(opt)}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
);

const MemoizedComponent = memo(({
    component,
    config,
    conversationId,
    gettingFullResponse
}: MemoizedComponentProps) => {

    const FallbackComponent = ({ component, content }: { component: any, content: any }) => {
        const displayContent = typeof component === 'string' ? component : JSON.stringify(component, null, 2);

        if (displayContent && displayContent.includes('\n')) {
            return (
                <div className={`font-light mt-6 mb-6 rounded-xl ${config ? "" : "bg-blue-100"} text-slate-800 p-4`} style={{ backgroundColor: `${config ? config.end_color : ""}` }}>
                    <code className="whitespace-pre-wrap break-words">{displayContent}</code>
                </div>
            );
        } else {
            return (
                <code className="border p-1 rounded-md bg-blue-100 text-slate-800 break-words">{displayContent}</code>
            );
        }
    };

    switch (component.type) {
        case 'calendly':
            return <Calendly
                url={component.calendly_url}
                key={component.calendly_url}
                gettingFullResponse={gettingFullResponse || false}
            />;
        case 'form_field':
            return <Form />;
        case 'form':
            return <Form />;
        case 'tidycal':
            return <TidyCal
                url={component.tidy_cal_url}
                key={component.tidy_cal_url}
                gettingFullResponse={gettingFullResponse || false}
            />;
        case 'hubspot_meetings':
            return <HubSpotMeetings
                url={component.hubspot_meetings_url}
                key={component.hubspot_meetings_url}
                gettingFullResponse={gettingFullResponse || false}
            />;
        case 'chili_piper':
            return <ChiliPiper
                domain={component.domain}
                inbound_router={component.inbound_router}
                session_id={conversationId || ''}
                email={component.email}
                key={component.url}
                gettingFullResponse={gettingFullResponse || false}
            />;
        case 'youtube':
            return <YouTube video_id={component.video_id} />;
        default:
            if (component.actions) {
                return (
                    <Actions
                        actions={component.actions}
                    />
                )
            } else {
                return <FallbackComponent
                    component={component.content || component}
                    content={component}
                />;
            }
    }
}, () => false);

// --- Inline citation chip + hover preview -----------------------------------

const CITATION_CARD_WIDTH = 240;
// Height budget used to decide whether the card fits above the chip.
const CITATION_CARD_FLIP_THRESHOLD = 170;
// Small hover-intent delay so sweeping the cursor across text doesn't flash cards.
const CITATION_CARD_OPEN_DELAY_MS = 150;

/** `https://docs.acme.com/pricing/` → host + path for the card header. */
const citationSite = (url: string): { host: string; prettyUrl: string } => {
    try {
        const parsed = new URL(url);
        const path = parsed.pathname.replace(/\/$/, '');
        return { host: parsed.hostname, prettyUrl: `${parsed.hostname}${path}` };
    } catch {
        return { host: '', prettyUrl: url };
    }
};

/**
 * Numbered inline source chip. Hovering (or keyboard-focusing) the chip pops
 * a small preview card — favicon, site URL, page title, and the result
 * snippet when the source carried one. The card is a body portal with fixed
 * positioning so transcript scroll containers can't clip it.
 */
const CitationChip: React.FC<{ cite: ShownCitation }> = ({ cite }) => {
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const openTimerRef = useRef<number | null>(null);
    const [card, setCard] = useState<{ top: number; left: number; above: boolean } | null>(null);
    const [faviconFailed, setFaviconFailed] = useState(false);
    const { host, prettyUrl } = citationSite(cite.url);

    const closeCard = () => {
        if (openTimerRef.current !== null) {
            window.clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        setCard(null);
    };

    const openCard = () => {
        if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current);
        openTimerRef.current = window.setTimeout(() => {
            const anchor = anchorRef.current;
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            const above = rect.top > CITATION_CARD_FLIP_THRESHOLD;
            const left = Math.min(
                Math.max(rect.left + rect.width / 2 - CITATION_CARD_WIDTH / 2, 8),
                Math.max(window.innerWidth - CITATION_CARD_WIDTH - 8, 8)
            );
            setCard({ top: above ? rect.top - 8 : rect.bottom + 8, left, above });
        }, CITATION_CARD_OPEN_DELAY_MS);
    };

    // The transcript scrolls out from under a fixed card — dismiss on scroll
    // rather than leave it trailing over unrelated content.
    useEffect(() => {
        if (!card) return;
        const dismiss = () => setCard(null);
        window.addEventListener('scroll', dismiss, true);
        return () => window.removeEventListener('scroll', dismiss, true);
    }, [card]);

    useEffect(() => () => {
        if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current);
    }, []);

    return (
        <>
            <a
                ref={anchorRef}
                href={cite.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Source ${cite.n}: ${cite.title || cite.url}`}
                className="inline-flex items-center justify-center min-w-[1.05rem] h-[1.05rem] px-[0.28rem] ml-[0.15rem] rounded-full bg-primary/10 text-primary text-[0.68rem] font-semibold leading-none align-[0.16em] no-underline ring-1 ring-inset ring-primary/20 hover:bg-primary/20 hover:ring-primary/35 focus-visible:outline-none focus-visible:bg-primary/20 transition-colors"
                onMouseEnter={openCard}
                onMouseLeave={closeCard}
                onFocus={openCard}
                onBlur={closeCard}
                onClick={closeCard}
            >
                {cite.n}
            </a>
            {card && createPortal(
                <div
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        top: card.top,
                        left: card.left,
                        width: CITATION_CARD_WIDTH,
                        transform: card.above ? 'translateY(-100%)' : 'none',
                        zIndex: 60,
                        pointerEvents: 'none',
                    }}
                >
                    <div className={`${card.above ? 'aimdoc-citation-card-above' : 'aimdoc-citation-card-below'} rounded-xl border bg-popover p-3 shadow-lg`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                            {host && !faviconFailed ? (
                                <img
                                    src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`}
                                    alt=""
                                    loading="lazy"
                                    className="w-4 h-4 rounded-[4px] flex-shrink-0"
                                    onError={() => setFaviconFailed(true)}
                                />
                            ) : (
                                <HeroIconSolid.GlobeAltIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                            )}
                            <span className="text-[11px] leading-none text-muted-foreground truncate">
                                {host || prettyUrl}
                            </span>
                        </div>
                        <div className="mt-1.5 text-[13px] leading-snug font-semibold text-popover-foreground line-clamp-2">
                            {cite.title || prettyUrl}
                        </div>
                        {cite.description ? (
                            <div className="mt-1 text-[12px] leading-snug text-muted-foreground line-clamp-3">
                                {cite.description}
                            </div>
                        ) : null}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

const Message: React.FC<MessageProps> = ({
    role,
    content,
    created_at,
    leadEmail,
    config,
    scrollToBottom,
    gettingResponse,
    gettingFullResponse,
    human_agent,
    isVisitorTyping,
    conversationId,
    message_metadata,
    messageId,
    agentName,
    visitorIsAiAgent,
    question,
    onQuestionChange,
    visitorQuestion,
    moderationScan,
    escalations
}) => {
    const [logo, setLogo] = useState<string>(aimdocLogo);
    const [contextOpen, setContextOpen] = useState<boolean>(false);
    // AI response messages carry a context snapshot id once captured (forward-only;
    // older messages won't have one). Human-agent replies are not AI generations.
    const hasContext = role === 'assistant' && !human_agent && !!messageId && !!message_metadata?.context_snapshot_id;
    // A linked knowledge-base question means the extraction run flagged this
    // reply (needs an answer) or a reviewer already corrected it. Either way the
    // drawer opens with the correction panel, even for replies with no snapshot.
    const linkedQuestion = role === 'assistant' && !human_agent && messageId ? question || null : null;
    // Reviewed = a person either wrote the answer or marked the flag not
    // applicable. Both close the loop, so both read green in the transcript.
    const questionDismissed = !!linkedQuestion && linkedQuestion.status === 'dismissed';
    const questionCorrected = !!linkedQuestion && (linkedQuestion.status === 'answered' || questionDismissed);
    const questionNeedsAnswer = !!linkedQuestion && !questionCorrected;
    const drawerAvailable = !!messageId && (hasContext || !!linkedQuestion);
    const preprocessJson = (jsonString: any) => {
        let correctedString = jsonString.replace(/\\'/g, "'");
        correctedString = correctedString.replace(/,(?=\s*[}\]])/g, "");
        return correctedString;
    }
    
    // Derive content instead of using state to avoid synchronization issues
    const newContent = useMemo(() => {
        return message_metadata?.task ? `Started task ${message_metadata?.task?.name}` : content;
    }, [message_metadata?.task, message_metadata?.task?.name, content]);

    const componentMapping: ComponentMapping = {
        calendly: Calendly,
        tidycal: TidyCal,
        hubspot_meetings: HubSpotMeetings,
        chili_piper: ChiliPiper,
        youtube: YouTube,
        form: Form,
        form_field: Form,
        actions: Actions
    };

    const getComponent = useMemo(() => (component: Component) => {
        return (
            <MemoizedComponent
                component={component}
                config={config}
                conversationId={conversationId || ''}
                gettingFullResponse={gettingFullResponse || false}
            />
        );
    }, [config, conversationId, gettingFullResponse]);

    const detectAndRenderUrls = (content: string): string => {
        if (!gettingFullResponse) {
            return content;
        }

        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const markdownUrlPattern = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g;
        const markdownStartPattern = /\[/;

        const lastOpenBracketIndex = content.lastIndexOf('[');

        if (lastOpenBracketIndex !== -1) {
            const substringFromLastBracket = content.slice(lastOpenBracketIndex);
            if (
                !markdownUrlPattern.test(substringFromLastBracket)
                && !endsWithCitationMarker(substringFromLastBracket)
            ) {
                return content.slice(0, lastOpenBracketIndex);
            }
        }

        return content;
    };

    const RenderChatComponent = (props: any) => {
        const hasLanguageClass = props.className && props.className.startsWith('language-');
        const isInline = props.inline === true || (props.inline !== false && !hasLanguageClass && typeof props.children === 'string' && !props.children.includes('\n'));
        const language = props.className?.replace('language-', '') || '';
        const codeContent = props.children;

        if (!isInline) {
            try {
                const clean = preprocessJson(codeContent);
                const component = JSON.parse(clean);

                if (Array.isArray(component)) {
                    return (
                        <>
                            {component.map((comp: any, index: number) => (
                                <React.Fragment key={index}>
                                    {getComponent(comp)}
                                </React.Fragment>
                            ))}
                        </>
                    );
                } else if (component && typeof component === 'object' && 'type' in component) {
                    return getComponent(component);
                }
            } catch (e) {
                // Not JSON — fall through to code block rendering
            }
        }

        return <CodeBlock code={codeContent} language={language} inline={isInline} />;
    }

    // Inline citations: `[n]` markers become numbered chips linking to the
    // resolved public source. Rows without `citations.shown` (still
    // streaming, or older) keep the marker as muted text.
    const citationsShown = shownCitations(message_metadata);
    const citationsDropped = droppedCitations(message_metadata);
    const citationMode = role === 'assistant' && Array.isArray(message_metadata?.citations?.shown);
    const CitationLink = (props: any) => {
        const n = parseCitationHref(props.href);
        if (n === null) {
            return baseMarkdownRenderers.a(props);
        }
        if (!citationMode) {
            // Visitor text, human replies, or rows without a resolved
            // citation list: `[n]` is literal text.
            return <span>[{n}]</span>;
        }
        const cite = citationsShown.find((c) => c.n === n);
        if (!cite) {
            return <span className="text-muted-foreground text-xs align-top">[{n}]</span>;
        }
        return <CitationChip cite={cite} />;
    };
    const markdownRenderers = {
        ...baseMarkdownRenderers,
        code: RenderChatComponent,
        a: CitationLink,
    };

    // Tool-call cards early-return before the standard message footer, so they
    // carry their own timestamp (right-aligned, matching assistant messages).
    const toolTimestamp = created_at ? (
        <span className="mt-1 mr-1 text-[11px] text-muted-foreground/70 leading-none">
            {formatDate(created_at)}
        </span>
    ) : null;

    const lifecycleTool = getToolLifecycleFromMetadata(message_metadata);
    if (role === 'assistant' && lifecycleTool) {
        const args = parseToolPayload(lifecycleTool.args);
        const output = parseToolPayload(lifecycleTool.output);
        const urls: string[] = Array.isArray(output?.urls)
            ? output.urls.filter((url: any) => typeof url === 'string' && url.length > 0)
            : [];

        let calendarComponent: Component | null = null;
        if (lifecycleTool.tool_name === 'show_booking_calendar') {
            const calendarType = args.calendar_type;
            const calendarUrl = args.calendar_url || args.calendly_url || args.tidy_cal_url;
            if (calendarType === 'calendly') {
                calendarComponent = { type: 'calendly', calendly_url: calendarUrl };
            } else if (calendarType === 'tidycal') {
                calendarComponent = { type: 'tidycal', tidy_cal_url: calendarUrl };
            } else if (calendarType === 'hubspot_meetings') {
                calendarComponent = { type: 'hubspot_meetings', hubspot_meetings_url: calendarUrl };
            } else if (calendarType === 'chili_piper') {
                calendarComponent = { type: 'chili_piper', email: args.email };
            }
        }

        const { failed: toolFailed, detail: toolErrorDetail, connection: toolErrorConnection } = getToolErrorState(output);
        const clientTool = getClientToolView(lifecycleTool, args, output);

        // Strip keys the card renders on its own from the generic output dump:
        // urls become links, and a failed MCP envelope is fully covered by the
        // error block (name in the header, server in the caption, body in the
        // pre), so dumping it again as JSON is pure noise.
        const displayOutput = toolFailed
            ? {}
            : (urls.length > 0
                ? (() => {
                    const { urls: _urls, ...rest } = output;
                    return rest;
                })()
                : output);

        // Offer "Analyze" on the tool call so customers can see why the AI decided
        // to call it. messageId presence = authenticated dashboard (gated in Chat);
        // the real envelope id for the snapshot is on the lifecycle state.
        const lifecycleHasContext = !!messageId && !!lifecycleTool.context_snapshot_id && !!lifecycleTool.message_id;

        // ask_question gets a readable card instead of the generic args/output dump.
        // The resolved questions on the tool output (what the visitor actually saw,
        // after field validation) win over the requested args.
        if (lifecycleTool.tool_name === 'ask_question') {
            const isQuestionList = (value: any): value is Array<Record<string, any>> =>
                Array.isArray(value) && value.length > 0 && value.every((q: any) => q && typeof q === 'object');
            const resolvedQuestions = isQuestionList(output?.questions)
                ? output.questions
                : (isQuestionList(args?.questions) ? args.questions : null);
            if (resolvedQuestions) {
                return (
                    <div className="flex flex-col items-end pb-1 w-full min-w-0">
                        <div className="mt-1 w-full max-w-[85%] min-w-0">
                            <AskQuestionToolCard
                                questions={resolvedQuestions}
                                onAnalyze={lifecycleHasContext ? () => setContextOpen(true) : undefined}
                            />
                        </div>
                        {toolTimestamp}
                        {lifecycleHasContext && lifecycleTool.message_id && (
                            <ContextDrawer
                                messageId={lifecycleTool.message_id}
                                open={contextOpen}
                                onClose={() => setContextOpen(false)}
                                toolCall={{ name: lifecycleTool.tool_name, args }}
                            />
                        )}
                    </div>
                );
            }
        }

        return (
            <div className="flex flex-col items-end pb-1 w-full min-w-0">
                <div className="mt-1 w-full max-w-[85%] min-w-0">
                    <ToolDetailCard
                        toolName={lifecycleTool.tool_name}
                        // A browser tool shows its own arguments and return
                        // value, not the dispatcher's wrapper or the envelope.
                        args={clientTool ? clientTool.input : args}
                        output={clientTool
                            ? (clientTool.result && typeof clientTool.result === 'object' ? clientTool.result : {})
                            : displayOutput}
                        rawOutput={clientTool
                            ? (typeof clientTool.result === 'string' ? clientTool.result : undefined)
                            : lifecycleTool.output}
                        urls={urls}
                        onAnalyze={lifecycleHasContext ? () => setContextOpen(true) : undefined}
                        failed={clientTool ? clientTool.failed : toolFailed}
                        failedLabel={clientTool ? clientTool.failedLabel : undefined}
                        errorDetail={clientTool ? clientTool.errorDetail : toolErrorDetail}
                        errorConnection={clientTool ? '' : toolErrorConnection}
                        clientTool={clientTool}
                        escalation={lifecycleTool.tool_name === 'escalate_to_human'
                            ? findEscalationForToolCall(escalations, lifecycleTool.call_id, lifecycleTool.started_at)
                            : null}
                    />

                    {calendarComponent && (
                        <div className="mt-1.5">
                            <MemoizedComponent
                                component={calendarComponent}
                                config={config}
                                conversationId={conversationId || ''}
                                gettingFullResponse={false}
                            />
                        </div>
                    )}

                </div>
                {toolTimestamp}
                {lifecycleHasContext && lifecycleTool.message_id && (
                    <ContextDrawer
                        messageId={lifecycleTool.message_id}
                        open={contextOpen}
                        onClose={() => setContextOpen(false)}
                        toolCall={{ name: lifecycleTool.tool_name, args }}
                    />
                )}
            </div>
        );
    }

    // Handle tool/function call messages (new and old structures)
    const functionCallsSafe: Array<{ name: string; arguments?: string }> = Array.isArray((message_metadata as any)?.function_calls)
        ? (message_metadata as any).function_calls
        : [];

    const assistantToolCalls: Array<{ name: string; arguments?: string }> = Array.isArray((message_metadata as any)?.tool_calls)
        ? ((message_metadata as any).tool_calls.map((tc: any) => ({
            name: tc?.function?.name,
            arguments: tc?.function?.arguments
        })) as Array<{ name: string; arguments?: string }>)
        : [];

    const filteredCalls = functionCallsSafe.filter(fc => fc && fc.name !== 'trigger_buyer_intent_notification');

    const effectiveCalls = (role === 'assistant' && assistantToolCalls.length > 0) ? assistantToolCalls : filteredCalls;

    if ((role === 'system' || role === 'assistant') && effectiveCalls.length > 0) {
        const renderCall = (fc: { name: string; arguments?: string }, index: number) => {
            const name = fc.name || '';
            let parsedArgs: Record<string, any> = {};

            try {
                if (fc.arguments) {
                    parsedArgs = typeof fc.arguments === 'string' ? JSON.parse(fc.arguments) : (typeof fc.arguments === 'object' ? fc.arguments as any : {});
                }
            } catch {}

            // Special case: readable question card instead of the raw payload
            if (name === 'ask_question' && Array.isArray(parsedArgs.questions) && parsedArgs.questions.length > 0) {
                return (
                    <AskQuestionToolCard
                        key={`${name}-${index}`}
                        questions={parsedArgs.questions.filter((q: any) => q && typeof q === 'object')}
                    />
                );
            }

            // Special case: calendar rendering
            if (name === 'show_booking_calendar') {
                const calendarType = parsedArgs.calendar_type;
                let calendarComponent = null;

                if (calendarType === 'calendly') {
                    calendarComponent = { type: 'calendly' as const, calendly_url: parsedArgs.calendar_url };
                } else if (calendarType === 'tidycal') {
                    calendarComponent = { type: 'tidycal' as const, tidy_cal_url: parsedArgs.calendar_url };
                } else if (calendarType === 'hubspot_meetings') {
                    calendarComponent = { type: 'hubspot_meetings' as const, hubspot_meetings_url: parsedArgs.calendar_url };
                } else if (calendarType === 'chili_piper') {
                    calendarComponent = { type: 'chili_piper' as const, email: parsedArgs.email };
                }

                if (calendarComponent) {
                    return (
                        <div key={`${name}-${index}`} className="w-full">
                            <MemoizedComponent
                                component={calendarComponent}
                                config={config}
                                conversationId={conversationId || ''}
                                gettingFullResponse={false}
                            />
                        </div>
                    );
                }
            }

            return (
                <ToolDetailCard
                    key={`${name}-${index}`}
                    toolName={name}
                    args={parsedArgs}
                />
            );
        };

        return (
            <div className="flex flex-col items-end pb-1 w-full min-w-0">
                <div className="mt-1 w-full max-w-[85%] min-w-0">
                    <div className="w-full min-w-0 space-y-1">
                        {effectiveCalls.map(renderCall)}
                    </div>
                </div>
                {toolTimestamp}
            </div>
        );
    }

    // Render attachments if present
    const renderAttachments = () => {
        const attachments = message_metadata?.attachments;
        if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
            return null;
        }

        return (
            <div className="mt-2 space-y-2">
                {attachments.map((attachment: any, index: number) => {
                    const isImage = attachment.type?.startsWith('image/');
                    const isPdf = attachment.type === 'application/pdf';
                    const isDoc = attachment.type?.includes('word') || attachment.type?.includes('document');
                    
                    const formatFileSize = (bytes: number) => {
                        if (bytes < 1024) return bytes + ' B';
                        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                    };

                    return (
                        <div key={index} className="border rounded-lg p-2 bg-background/50">
                            {isImage ? (
                                <div className="space-y-2">
                                    <img 
                                        src={attachment.url} 
                                        alt={attachment.filename}
                                        className="max-w-full rounded cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(attachment.url, '_blank')}
                                        style={{ maxHeight: '300px' }}
                                    />
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span>{attachment.filename}</span>
                                        <span>{formatFileSize(attachment.size)}</span>
                                    </div>
                                </div>
                            ) : (
                                <a 
                                    href={attachment.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 hover:bg-accent rounded p-1 transition-colors"
                                >
                                    <div className="flex-shrink-0">
                                        {isPdf && <FileText className="h-8 w-8 text-red-500" />}
                                        {isDoc && <FileText className="h-8 w-8 text-blue-500" />}
                                        {!isPdf && !isDoc && <FileIcon className="h-8 w-8 text-gray-500" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{attachment.filename}</p>
                                        <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
                                    </div>
                                    <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="flex flex-col pb-2 w-full min-w-0">
            <div
                key={content}
                className={cn(
                    "flex gap-2 w-full min-w-0 items-end",
                    role === 'assistant' ? 'pl-12 justify-end' : 'mr-4'
                )}
            >
                {role !== 'assistant' && (
                    <Avatar className="h-7 w-7 flex-shrink-0" title={visitorIsAiAgent ? 'External AI agent' : undefined}>
                        <AvatarFallback className={cn(
                            "border",
                            visitorIsAiAgent
                                ? "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30"
                                : "bg-muted text-muted-foreground border-border"
                        )}>
                            {visitorIsAiAgent ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                        </AvatarFallback>
                    </Avatar>
                )}
                <Card
                    className={cn(
                        "relative text-sm min-w-0 max-w-[85%] overflow-hidden shadow-none",
                        role === 'assistant'
                            ? "rounded-2xl rounded-br-sm bg-muted/50 border-border"
                            : "rounded-2xl rounded-bl-sm bg-muted border-border",
                        gettingResponse && role !== 'assistant' && "bg-muted/60",
                        questionNeedsAnswer && "border-amber-500/60 ring-1 ring-inset ring-amber-500/20",
                        questionCorrected && "border-emerald-500/60 ring-1 ring-inset ring-emerald-500/20"
                    )}
                >
                    {isVisitorTyping ? (
                        <div className='flex space-x-2 justify-center items-center px-4 py-3'>
                            <div className='h-1.5 w-1.5 rounded-full animate-bounce [animation-delay:-0.3s] bg-muted-foreground/50'></div>
                            <div className='h-1.5 w-1.5 rounded-full animate-bounce [animation-delay:-0.15s] bg-muted-foreground/50'></div>
                            <div className='h-1.5 w-1.5 rounded-full animate-bounce bg-muted-foreground/50'></div>
                        </div>
                    ) : (
                        <>
                            {role !== 'assistant' && visitorIsAiAgent && (
                                <div className="flex items-center gap-1.5 border-b border-border/60 px-3.5 py-1.5">
                                    <Bot className="h-3 w-3 flex-shrink-0 text-cyan-600 dark:text-cyan-400" />
                                    <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">AI Agent</span>
                                </div>
                            )}
                            {role === 'assistant' && !human_agent && (
                                <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3.5 py-1.5">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <AimdocMark className="flex-shrink-0 text-primary/50" />
                                        <span className="text-xs font-medium text-foreground/80 truncate">{agentName || 'AI Agent'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {questionNeedsAnswer && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => setContextOpen(true)}
                                                className="h-6 gap-1 rounded-full bg-amber-500/15 px-2 text-[11px] font-medium text-amber-700 hover:bg-amber-500/25 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200 [&_svg]:size-3"
                                                title={linkedQuestion?.extraction_reason
                                                    ? `Flagged: ${linkedQuestion.extraction_reason}`
                                                    : 'The AI could not answer this. Provide the correct answer.'}
                                            >
                                                <AlertTriangle />
                                                Needs answer
                                            </Button>
                                        )}
                                        {questionCorrected && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => setContextOpen(true)}
                                                className="h-6 gap-1 rounded-full bg-emerald-500/15 px-2 text-[11px] font-medium text-emerald-700 hover:bg-emerald-500/25 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200 [&_svg]:size-3"
                                                title={linkedQuestion?.answered_by
                                                    ? `${questionDismissed ? 'Marked not applicable' : 'Corrected'} by ${[linkedQuestion.answered_by.first_name, linkedQuestion.answered_by.last_name].filter(Boolean).join(' ') || linkedQuestion.answered_by.email || 'a teammate'}`
                                                    : (questionDismissed ? 'Reviewed: the flag was not applicable' : 'A corrected answer is saved for this question')}
                                            >
                                                <CheckCircle2 />
                                                {questionDismissed ? 'Reviewed' : 'Answer corrected'}
                                            </Button>
                                        )}
                                        {moderationScan?.flagged && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => setContextOpen(true)}
                                                className="h-6 gap-1 rounded-full border border-orange-500/60 px-2 text-[11px] font-medium text-orange-600 hover:bg-orange-500/10 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 flex-shrink-0 [&_svg]:size-3"
                                                title={`${moderationScan.review_run_id ? 'Flagged by session moderation and sent for review' : 'Flagged by session moderation'}: ${moderationCategoryLabel(moderationScan.categories?.[0])}`}
                                            >
                                                <Flag />
                                                Flagged
                                            </Button>
                                        )}
                                        {hasContext && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => setContextOpen(true)}
                                                className="h-6 gap-1 rounded-full bg-primary/10 px-2 text-[11px] font-medium text-primary hover:bg-primary/20 hover:text-primary flex-shrink-0 [&_svg]:size-3"
                                                title="Analyze the context the AI received for this response"
                                            >
                                                <Sparkles />
                                                Analyze
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="px-3.5 py-2 min-w-0 break-words [overflow-wrap:anywhere]">
                                <Markdown
                                    remarkPlugins={[remarkBreaks, remarkCitations]}
                                    children={detectAndRenderUrls(newContent)}
                                    components={markdownRenderers}
                                />
                                {(citationsShown.length > 0 || citationsDropped.length > 0) && (
                                    <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                                        {citationsShown.map((cite) => (
                                            <div key={`shown-${cite.n}`} className="flex items-start gap-1.5 min-w-0">
                                                <span className="shrink-0 font-semibold text-primary">[{cite.n}]</span>
                                                <a
                                                    href={cite.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="truncate hover:text-foreground hover:underline"
                                                    title={cite.url}
                                                >
                                                    {cite.title || cite.url}
                                                </a>
                                            </div>
                                        ))}
                                        {citationsDropped.map((dropped) => (
                                            <div key={`dropped-${dropped.marker}`} className="flex items-start gap-1.5 min-w-0">
                                                <span className="shrink-0 font-semibold">[{dropped.marker}]</span>
                                                <span className="truncate" title={dropped.reason === 'internal' ? 'Internal source — cited by the AI, hidden from the visitor' : 'The AI cited a source number this turn never produced'}>
                                                    {dropped.reason === 'internal'
                                                        ? `${dropped.kind === 'qa' ? 'Q&A' : 'File'}: ${dropped.label || 'internal source'} (hidden)`
                                                        : 'Unresolved citation (removed)'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {renderAttachments()}
                            </div>
                        </>
                    )}
                    {role === 'system' && (
                        <div className="px-3.5 pb-2">
                            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                System
                            </span>
                        </div>
                    )}
                </Card>
                {message_metadata?.task && (
                    <div
                        className="flex-shrink-0 text-xs text-foreground font-semibold bg-background px-2 py-1 rounded-md border border-border flex items-center gap-1.5 group relative"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            className="w-5 h-5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                            {message_metadata?.task?.name}
                        </div>
                    </div>
                )}
                {role === 'assistant' && human_agent && (
                    <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarImage src={human_agent.profile_photo_url || undefined} alt={human_agent.name || human_agent.email || 'Rep'} />
                        <AvatarFallback className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 text-xs font-medium">
                            {(`${human_agent.first_name?.[0] || ''}${human_agent.last_name?.[0] || ''}` || human_agent.email?.[0] || 'A').toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                )}
            </div>
            {created_at && (
                <div className={cn(
                    "mt-1 flex items-center gap-1.5",
                    role === 'assistant' ? 'justify-end mr-1' : 'justify-start ml-11'
                )}>
                    {message_metadata?.from_voice && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400" title="Voice message">
                            <Phone className="h-3 w-3" />
                        </span>
                    )}
                    {role === 'assistant' && !human_agent && message_metadata?.stopped && (
                        <span
                            className="inline-flex items-center gap-1 text-[11px] leading-none text-amber-600 dark:text-amber-400"
                            title="The visitor pressed Stop before this reply finished; the text above is what they saw."
                        >
                            <CircleStop className="h-3 w-3" />
                            Stopped by visitor
                        </span>
                    )}
                    <span className="text-[11px] text-muted-foreground/70 leading-none">
                        {formatDate(created_at)}
                    </span>
                </div>
            )}
            {drawerAvailable && messageId && (
                <ContextDrawer
                    messageId={messageId}
                    open={contextOpen}
                    onClose={() => setContextOpen(false)}
                    responseContent={content}
                    hasSnapshot={hasContext}
                    question={linkedQuestion}
                    onQuestionChange={onQuestionChange}
                    visitorQuestion={visitorQuestion}
                />
            )}
        </div>
    );
};

export default Message;